import { useEffect, useMemo, useRef, useState } from 'react';
import {
    getPoplachMockData,
    type MockVariant,
    type PoplachMockData,
    type PoplachMockOverrides,
} from '../Pages/mock/poplachMockData';

const TICK_MS = 1000;

export const usePoplachMockData = (
    mockType: string | null,
    overrides?: PoplachMockOverrides,
): PoplachMockData | null => {
    const [data, setData] = useState<PoplachMockData | null>(null);

    // Stabilizace overrides — předáváme objekt přes useMemo dle hodnot, ne reference,
    // aby useEffect neresetoval interval při každém renderu rodiče.
    const stableOverrides = useMemo<PoplachMockOverrides>(
        () => ({
            technika: overrides?.technika,
            jedu: overrides?.jedu,
            nejedu: overrides?.nejedu,
            posily: overrides?.posily,
            mapaLayout: overrides?.mapaLayout,
            mapMode: overrides?.mapMode,
            fixedZoom: overrides?.fixedZoom,
            zoom1: overrides?.zoom1,
            zoom2: overrides?.zoom2,
            interval: overrides?.interval,
            pred: overrides?.pred,
            udalost: overrides?.udalost,
            popis: overrides?.popis,
            upresneni: overrides?.upresneni,
            nocniRezim: overrides?.nocniRezim,
            pravidla: overrides?.pravidla,
        }),
        [overrides?.technika, overrides?.jedu, overrides?.nejedu, overrides?.posily,
         overrides?.mapaLayout, overrides?.mapMode, overrides?.fixedZoom,
         overrides?.zoom1, overrides?.zoom2, overrides?.interval,
         overrides?.pred, overrides?.udalost, overrides?.popis,
         overrides?.upresneni, overrides?.nocniRezim, overrides?.pravidla],
    );

    // sessionStartMs musí přežít změny stableOverrides (např. když admin přepne mapaLayout/mapMode/interval),
    // jinak by se `casVyhlaseni` ve mock dat resetovalo a odpočet skočil zpět na -02:00.
    // Reset jen při změně mockType (nebo když mock vypneme).
    const startedAtRef = useRef<number | null>(null);

    useEffect(() => {
        if (!mockType) {
            setData(null);
            startedAtRef.current = null;
            return;
        }
        const variant = mockType as MockVariant;
        if (startedAtRef.current === null) {
            startedAtRef.current = Date.now();
        }
        const startedAt = startedAtRef.current;
        const update = () => {
            const elapsed = Date.now() - startedAt;
            setData(getPoplachMockData(variant, elapsed, startedAt, stableOverrides));
        };
        update(); // initial
        const interval = setInterval(update, TICK_MS);
        return () => clearInterval(interval);
    }, [mockType, stableOverrides]);

    return data;
};
